import dayjs, { ConfigType, Dayjs, QUnitType, UnitTypeLong } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import minMax from 'dayjs/plugin/minMax';
import localeData from 'dayjs/plugin/localeData';
import duration from 'dayjs/plugin/duration';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isToday from 'dayjs/plugin/isToday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { has, isNil, isObject, map } from './lodash';
import { TIME_FORMAT } from '../constants';
import { TIMEZONE_KEY } from '../interfaces';

dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(minMax);
dayjs.extend(quarterOfYear);
dayjs.extend(isToday);
dayjs.extend(weekOfYear);
dayjs.extend(objectSupport);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/**
 *  dayjs 生成的星期排序是 ['日', '一'...], 需要调整一下顺序
 *
 * @param {any[]} weekdays ['日', '一'...]
 * @returns
 */
const translateWeekdays = (weekdays: any[]) => {
    return map(Array(7), (_, index) => weekdays[(index + 1) % 7]);
};

/**
 * fix: 校验时区偏移是否正确【dayjs bug，先临时修复】
 *
 * 在0点调整过夏令时的时间点，在时区转换时可能存在问题，如：开罗(Africa/Cairo) 在 2025.10.31 实行了冬令时（utc+3 -> utc+2）
 * 客户端时区设置为开罗，执行 dayjs("2025-10-30T16:00:00.000Z").tz("Etc/GMT-8").utcOffset() = 540(utc+9)，预期结果应该是 480(utc+8)
 * 修正逻辑：取该特殊时间点的前后各2天日期计算时区，如果 utc 偏移不一致，则表示时区偏移存在问题，需要修正
 *
 * https://github.com/iamkun/dayjs/issues/2303
 * https://project.feishu.cn/b2rl2h/assignment/detail/6450250162
 *
 * @param dayjsTzDate 转换过时区的 dayjs 对象
 * @param timezone 要转换的时区
 */
const isTimeZoneCorrect = (dayjsTzDate: Dayjs, timezone: TIMEZONE_KEY) => {
    let timeZones = [];
    for (const i of [-2, -1, 1, 2]) {
        // in order to judge the time, we use two groups of forward/backward time's offset
        timeZones.push(dayjsTzDate.add(i, 'day').format('YYYY-MM-DD HH:mm:ss'));
    }
    timeZones = timeZones.map(function (i) {
        // only use those timeoffset info
        return dayjs.tz(i, timezone).format('Z');
    });
    if ([...new Set(timeZones)].length > 2) {
        // if N > 2, it means there are wrong times surround current time, but current input time must be true
        return true;
    }

    return timeZones.includes(dayjsTzDate.format('Z')); // if our time offset in this group, it means correct time offset
};

const repairErrorTimeOffset = (dayjsTzDate: Dayjs, timezone: TIMEZONE_KEY) => {
    const correctOffset = dayjs.tz(dayjsTzDate.format(), timezone).utcOffset();
    return dayjsTzDate.utcOffset(correctOffset, true);
};

const transTimezone = (value: Dayjs, timezone?: TIMEZONE_KEY) => {
    if (timezone && value.isValid()) {
        const tzValue = value.tz(timezone);
        try {
            if (!isTimeZoneCorrect(tzValue, timezone)) {
                return repairErrorTimeOffset(tzValue, timezone);
            }
            return tzValue;
        } catch {
            return tzValue;
        }
    }
    return value;
};

const localDateToTimezoneDate = (date: Date, timezone?: TIMEZONE_KEY) => {
    if (timezone) {
        const timeZoneDate = dayjs.tz(date, timezone).format(TIME_FORMAT);
        return dayjs(timeZoneDate).toDate();
    }
    return date;
};

const dayjsUnit = ['year', 'month', 'date', 'hour', 'minute', 'second', 'millisecond'] as const;

type DateValue = string | number | undefined | null;

const dayjsUtils = {
    // get
    getYear: (date: Dayjs) => date.year(),
    getMonth: (date: Dayjs) => date.month(),
    getDate: (date: Dayjs) => date.date(),
    getHour: (date: Dayjs) => date.hour(),
    getMinute: (date: Dayjs) => date.minute(),
    getSecond: (date: Dayjs) => date.second(),
    // set -change origin
    setYear: (date: Dayjs, year: number) => date.year(year),
    setMonth: (date: Dayjs, month: number) => date.month(month),
    setDate: (date: Dayjs, num: number) => date.date(num),
    setHour: (date: Dayjs, hour: number, minute?: number, second?: number) => {
        let value = date.hour(hour);
        if (!isNil(minute)) {
            value = value.minute(minute);
        }
        if (!isNil(second)) {
            value = value.second(second);
        }
        return value;
    },
    setMinute: (date: Dayjs, minute: number) => date.minute(minute),
    setSecond: (date: Dayjs, second: number) => date.second(second),
    setMillisecond: (date: Dayjs, millisecond: number) => date.millisecond(millisecond),

    isAfter: (date1: Dayjs, date2: Dayjs) => date1.isAfter(date2),

    localDateToTimezoneDate: localDateToTimezoneDate,
    isDayjs: (value: any) => {
        return dayjs.isDayjs(value);
    },
    utcToTimezone: (date: ConfigType, timezone?: TIMEZONE_KEY) => {
        // 将 date 转为标准 utc 格式字符串再进行转换处理
        // 即如果没有设置时区，按照客户端时区转换
        return transTimezone(dayjs(dayjs.utc(date).format()), timezone);
    },
    getNow: (timezone?: TIMEZONE_KEY) => {
        return transTimezone(dayjs(), timezone);
    },
    getDay: (date: Dayjs) => date.day(),
    getMillisecond: (date: Dayjs) => date.millisecond(),
    getRealTimeZoneDayjs: (date: Date, timezone?: string) => {
        const dayjsDate = dayjs(date);
        if (timezone && dayjsDate.isValid()) {
            const dateStr = dayjsDate.format(TIME_FORMAT);
            return dayjs.tz(dateStr, timezone);
        }
        return dayjsDate;
    },
    dayjs: (date: ConfigType, timezone?: TIMEZONE_KEY) => {
        if (
            isObject(date) &&
            !(dayjsUtils.isDayjs(date) || date instanceof Date) &&
            has(date, 'year')
        ) {
            /**
             * 将传入的 date 按照 new Date() 接受参数的顺序转为数组：
             * { year?:any,month?:any,date?:any,hour?:any,minute?:any,second?:any } -> ['year', 'month', 'date', 'hour', 'minute', 'second', 'millisecond']
             */
            const params: DateValue[] = [];
            dayjsUnit.forEach((unit) => {
                if (has(date, unit)) {
                    params.push((date as Record<UnitTypeLong, DateValue>)[unit]);
                }
            });

            /**
             * 时区转换：先创建 new Date，再输出日期字符串(yyyy-MM-dd HH:mm:ss)，传入 dayjs.tz(dateStr, timezone) 处理时区
             * 先创建 Date 再转字符串，可以在传入特殊值（null/undefined/NaN）时，行为与 new Date 保持一致（默认 dayjs 的处理和 Date 是不一样的）
             */
            // @ts-ignore
            return dayjsUtils.getRealTimeZoneDayjs(new Date(...params), timezone);
        }
        return transTimezone(dayjs(date), timezone);
    },
    setFullYear: (v: Dayjs, year: number, month?: number, date?: number) => {
        let value = v.year(year);
        if (!isNil(month)) {
            value = value.month(month as number);
        }
        if (!isNil(date)) {
            value = value.date(date as number);
        }
        return value;
    },
    format: (date: Dayjs | null, format?: string) => {
        if (isNil(date)) {
            return '';
        }
        return date.locale('en').format(format);
    },
    tz: (date: ConfigType, timezone?: TIMEZONE_KEY) => {
        if (timezone) {
            return dayjs.tz(date, timezone);
        }
        return dayjs(date);
    },
    isValidate: (date: Dayjs) => date.isValid(),
    getWeekStartDate: (date: Dayjs) => date.startOf('week'),
    getWeekEndDate: (date: Dayjs) => date.endOf('week'),
    getMonthStartDate: (date: Dayjs) => date.startOf('month'),
    getMonthEndDate: (date: Dayjs) => date.endOf('month'),
    valueOf: (date: Dayjs) => date.valueOf(),

    addYear: (date: Dayjs, diff: number) => date.add(diff, 'year'),
    addMonth: (date: Dayjs, diff: number) => date.add(diff, 'month'),
    addDate: (date: Dayjs, diff: number) => date.add(diff, 'day'),
    addWeek: (date: Dayjs, diff: number) => date.add(diff, 'week'),

    getWeekdaysMin: () => translateWeekdays(dayjs().localeData().weekdaysMin()),
    getWeekdaysShort: () => translateWeekdays(dayjs().localeData().weekdaysShort()),
    localeData: () => dayjs().localeData(),
    getWeekdays: () => translateWeekdays(dayjs().localeData().weekdays()),

    isSameDate: (date1: Dayjs, date2: Dayjs) => date1.isSame(date2, 'date'),
    isSameMonth: (date1: Dayjs, date2: Dayjs) => date1.isSame(date2, 'month'),
    isBefore: (date1: Dayjs, date2: Dayjs, unit?: QUnitType) => date1.isBefore(date2, unit)
};

/**
 * 获取用户当前时区和UTC偏移量
 */
const getTimezone = () => {
    const timezone = dayjs.tz.guess(); // 获取本地时区名称 ，例如 "Asia/Shanghai"
    try {
        // timezone 可能为 'Etc/Unknown'，导致 dayjs().tz(timezone) 出错（https://github.com/tc39/proposal-temporal/issues/2510）
        const offset = dayjs().tz(timezone).format('Z'); // 获取当前时区相对于 UTC 的偏移量，格式为 +08:00
        const sign = offset[0] === '-' ? '+' : '-'; // 判断偏移量的正负号
        const hours = parseInt(offset.substr(1, 2), 10); // 截取小时数
        const gmtOffset = `Etc/GMT${sign}${hours}`; // 拼接最终的字符串
        return {
            label: timezone,
            gmt_offset: gmtOffset
        };
    } catch (e) {
        return { label: timezone };
    }
};

export { dayjs, dayjsUtils, Dayjs as DayjsType, getTimezone };

export const notSupportTimezone = (timezone?: TIMEZONE_KEY) => {
    if (!timezone) {
        return false;
    }
    try {
        dayjs().tz(timezone);
        dayjs.tz(dayjs().format(TIME_FORMAT), timezone);
        return false;
    } catch {
        return true;
    }
};
